import { useContext, useEffect, useState } from 'react';
import ViewInspection from '../Inspections/View';
import { DeleteInspectionGroupDocument, GetInspectionsDocument, GetInspectionsQuery, InspectionDueType } from 'graphql/generated';
import { CheckCircleIcon, ClockIcon, Cog8ToothIcon, ExclamationCircleIcon, TrashIcon } from '@heroicons/react/24/solid';
import Edit from 'views/Part91/Aircraft/Status/Edit';
import EditInspectionFlyout from '../Inspections/Edit';
import { CalendarDateIcon, CaretDownIcon, CaretRightIcon, PlusIcon } from 'assets/icons';
import Button from 'components/Button/Button';
import NewInspectionFlyout from '../Inspections/New';
import ProgressRing from 'components/ProgressRing/ProgressRing';
import Modal from 'components/Modal/Modal';
import { useMutation } from '@apollo/client';
import { useSession } from 'contexts';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { AbilityContext } from 'contexts/AbilityContext';
import { permissionHandler } from 'utils/PermissionHandler';
import { WalkthroughContext } from 'views/Part91/Walkthrough';


export default function InspectionGroup({
  group,
  updateFlyout,
  closeFlyout,
  disableAdding,
  ...props
} : {
  updateFlyout: (flyoutData: { title: string; content: JSX.Element }) => void;
  closeFlyout: () => void;
  group: {
    icon: ({ className }: { className: any }) => JSX.Element;
    color: string;
    title: string;
    note: string;
    inspections: GetInspectionsQuery['craft']['inspectionGroups'][number]['inspections'];
    id: string;
  };
  disableAdding?: boolean;
} & { [key: string]: any }) {
  const { craftId } = useSession();
  // Initialize isOpen state from localStorage, defaulting to false (collapsed)
  const [isOpen, setIsOpen] = useState(() => {
    const storageKey = `inspectionGroup_${group.id}_expanded`;
    const stored = localStorage.getItem(storageKey);
    return stored === 'true';
  });
  const [overdue, setOverdue] = useState(0);
  const [upcoming, setUpcoming] = useState(0);
  const [ showModal, setShowModal ] = useState(false);
  const [ showPaywall, setShowPaywall ] = useState(false);
  const ability = useContext(AbilityContext);
  const { formStep, changeStep } = useContext(WalkthroughContext);
  const canManageInspections = permissionHandler('Manage Inspections', ability);
  const [deleteInspectionGroup] = useMutation(DeleteInspectionGroupDocument, { refetchQueries: [{ query: GetInspectionsDocument, variables: { craftId } }] });
  
  // Persist the expansion state to localStorage whenever it changes
  useEffect(() => {
    const storageKey = `inspectionGroup_${group.id}_expanded`;
    localStorage.setItem(storageKey, isOpen.toString());
  }, [isOpen, group.id]);
  
  useEffect(() => {
    let tempOverdue = 0;
    let tempUpcoming = 0;
    group.inspections?.forEach((inspection) => {      
      if (inspection?.due) {
        tempOverdue +=1;
      } else if (inspection?.almostDue) {
        tempUpcoming += 1;
      }
    });
    setOverdue(tempOverdue);
    setUpcoming(tempUpcoming);
  }, [group.inspections]);
  return (
    <>
      {showModal && <Modal message={`You are about to delete the ${group?.title} inspection group`} cancel={setShowModal} accept={() => deleteInspectionGroup({ variables: { id: group.id } })} /> }
      {showPaywall && <Modal message={`Adding more inspections and importing ADS is a premium feature`} cancel={setShowModal} accept={()=>{}} /> }
      <div className={`${isOpen ? 'shadow-blue' : ''}`}>
        {/* COLLAPSED VIEW */}
        <div
          onClick={() => setIsOpen(!isOpen)}
          className={`flex flex-row justify-between md:items-center px-2 md:px-4 p-4 pr-10 md:pr-12 w-full bg-white relative z-8 cursor-pointer border border-slate-200 ${isOpen ? 'rounded-t' : 'rounded'}`}>
          <div className="flex md:w-3/5 items-center">
            <group.icon className="h-8 w-8 p-2 text-brand-electric bg-brand-pale rounded" />
            <div className="ml-2 flex flex-row justify-center items-center gap-2">
              <h1 className="font-bold text-lg md:text-xl">{group.title}</h1>
              <span className="ml-0 text-sm">{`(${group.inspections.length})`}</span>
            </div>
          </div>
          <div className="flex md:w-2/5 justify-between items-center">
            <span className="hidden md:block ml-0 text-sm">{group.note}</span>
            
            <div className="flex items-center grow md:grow-0">
              <div
                className={`${
                  upcoming ? 'flex' : 'hidden'
                } justify-center items-center h-8 md:w-8 p-2 bg-yellow-100 rounded mx-1 font-semibold text-yellow-500 text-xs md:text-sm`}>
                <span>{upcoming}</span>
              </div>
              <div
                className={`${
                  overdue ? 'flex' : 'hidden'
                } justify-center items-center h-8 md:w-8 p-2 bg-red-100 rounded mx-1 font-semibold text-red-500 text-xs md:text-sm`}>
                <span>{overdue}</span>
              </div>
            </div>
          </div>
          <CaretDownIcon className={`${isOpen ? '' : 'hidden'} h-4 w-4 ml-4 absolute top-6 md:top-auto right-4`} />
          <CaretRightIcon className={`${isOpen ? 'hidden' : ''} h-4 w-4 ml-4 absolute top-6 md:top-auto right-4`} />
        </div>

        {/* OPENED BOX */}
        <div className={`${isOpen ? '' : 'hidden'} border-x border-b border-slate-200 pt-2 bg-white rounded-b shadow-inner inner-shadow`}>
          {/* INSPECTIONS */}
         { group.inspections.length !== 0?  <div className="">
            {[...group.inspections].sort((a,b) => a.position - b.position)?.map((inspection, index) => {
              const amountValue = Number(inspection?.amountLeft?.[0]);
              const amountUnit = inspection?.amountLeft?.[1];
              const doesNeedSetup = (!inspection.lastPerformed && !inspection.lastPerformedInterval);
              return (
                <div
                  key={index}
                  onClick={() => { 
                    if(inspection.dueMethod === InspectionDueType.Unknown) return updateFlyout({ title: "Inspection Setup", content: <EditInspectionFlyout id={inspection.id} closeFlyout={closeFlyout} /> });
                    updateFlyout({ title: "Inspection Entries", content: <ViewInspection closeFlyout={closeFlyout} id={inspection.id} /> });
                    if(formStep === "selectAnnualInspection" && inspection.name === "Annual") return changeStep('selectAnnualInspection', 'setupAnnualInspection', false);
                  }}
                  className={`flex flex-col bg-white w-full rounded cursor-pointer hover:bg-slate-50 transition px-2 md:px-4 ${formStep === "selectAnnualInspection" && inspection.name === "Annual" && " relative z-[102]"}`}>
                  <div className="flex p-2 justify-between gap-2 items-center w-full border-b border-slate-200">
                    <div className="md:w-3/5">
                      <div className='flex'>
                        <h1 className="font-bold text-brand-dark text-lg md:mb-0">{inspection.name}</h1>
                      </div>
                      <div className="flex items-center text-xs md:text-sm font-medium te">
                        { inspection.dueMethod !== InspectionDueType.Interval && <CalendarDateIcon className="h-5 w-5 font-bold" /> }
                        { inspection.dueMethod !== InspectionDueType.Date && <ClockIcon className="h-5 w-5 font-bold" /> }
                        <span className="mx-1">Due: {inspection.nextDueText}{inspection?.airworthinessItem ? "*" : '' }</span>
                      </div>
                    </div>
                    
                    {inspection?.doesRepeat !== false ? 
                      <>
                        { doesNeedSetup ? 
                            <span className="font-semibold">Needs Setup</span>
                          : <div className="flex flex-col justify-end items-center">
                            <ProgressRing radius={35} stroke={5} warning={inspection.almostDue} progress={inspection?.nextDuePercentage} value={amountValue}  />
                            <span className="font-bold">{amountUnit}</span> 
                          </div> }
                      </>
                    : <>
                      
                      {inspection.lastPerformed ?
                        <CheckCircleIcon className="h-8 w-8 text-green-500" />
                      :
                        <div className='flex flex-col self-center items-center'>
                          {inspection.due ?
                            <ExclamationCircleIcon className="h-8 w-8 text-red-500" />
                            :
                            <></>
                          }
                          { inspection.dueMethod === InspectionDueType.Unknown && <span className="font-bold">Needs Setup</span>}
                        </div>
                      }
                      
                    </>}
                  </div>
                </div>
              );
            })}
          </div> : 
            <div className="p-6 flex justify-center items-center bg-pale w-full rounded border-b">
              <span>No Inspections</span>
            </div>
          }
          {/* BOTTOM BAR */}
          <div className="py-2 bg-white w-full rounded-b flex justify-end items-center gap-2 px-2 md:px-4">
            {/* <Button 
              onClick={(e)=> {
                e.stopPropagation();
                updateFlyout({ title: group.title, content: <Edit id={group.id} closeFlyout={closeFlyout} /> });
              }}
              icon={<Cog8ToothIcon className="h-4 w-4 mr-1"/>} 
              text="Configure"
              size="xs"
              color="offwhite"
            /> */}
            {canManageInspections && <Button 
              onClick={() => {
                if(disableAdding){
                  setShowPaywall(true);
                }else {
                  updateFlyout({ title: 'Add Inspection', content: <NewInspectionFlyout closeFlyout={closeFlyout} groupId={group.id} /> });
                }
              }}
              icon={disableAdding ? <LockClosedIcon className="h-3 w-3"/> : <PlusIcon className="h-3 w-3" /> }
              text=""
              className={disableAdding ? 'opacity-50' : ''}
              color='pale'
              size='xs'/> }
              <Cog8ToothIcon className="h-8 w-8 md:w-12 p-2 bg-slate-100 rounded" onClick={ (e)=> {
                e.stopPropagation();
                updateFlyout({ title: group.title, content: <Edit id={group.id} closeFlyout={closeFlyout} /> });
              } } />
            { group.inspections.length == 0 && canManageInspections &&
              <TrashIcon
                  onClick={() => { setShowModal(true)}}
                  title="Delete"
                  className="text-rose-500 bg-rose-50 w-8 h-8 md:w-12 p-1.5 border border-rose-200 rounded cursor-pointer hover:bg-rose-50 transition"
              /> 
            }
          </div>
        </div>
      </div>
    </>
  );
}
